import { faGasPump } from '@fortawesome/pro-solid-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import type { GetVcPnlResponse } from '@api/features/voyages/getVcPnl';

const getVcPnlItems = (
	pnl: GetVcPnlResponse,
	handleModalOpen: () => void,
	showTcInDeliveryBunkers?: boolean,
	bunkersGoBelowZero?: boolean,
	hasCargoRelet?: boolean,
	isTcInVessel?: boolean,
	isEuEtsIceClassDiscounted?: boolean,
) => {
	const revenueItems = hasCargoRelet ? [
		{
			item: 'Freight VC-Out',
			...pnl.freightVcOut,
		},
		{
			item: 'Freight VC-In',
			...pnl.freightVcIn,
		},
		{
			item: 'Misc. Revenue',
			...pnl.otherRevenue,
		},
		{
			item: 'Demurrage VC-Out',
			...pnl.demurrageVcOut,
		},
		{
			item: 'Demurrage VC-In',
			...pnl.demurrageVcIn,
		},
		{
			item: 'Despatch VC-Out',
			...pnl.despatchVcOut,
		},
		{
			item: 'Despatch VC-In',
			...pnl.despatchVcIn,
		},
		{
			item: 'Addcomm. VC-Out',
			...pnl.addressCommissionVcOut,
		},
		{
			item: 'Addcomm. VC-In',
			...pnl.addressCommissionVcIn,
		},
		{
			item: 'Broker comm. VC-Out',
			...pnl.brokerCommissionVcOut,
		},
		{
			item: 'Broker comm. VC-In',
			...pnl.brokerCommissionVcIn,
		},
		{
			item: 'Net Revenue',
			bold: true,
			...pnl.totalRevenue,
		},
	] : [
		{
			item: 'Freight',
			...pnl.freight,
		},
		{
			item: 'Other Revenue',
			...pnl.otherRevenue,
		},
		{
			item: 'Demurrage',
			...pnl.demurrage,
		},
		{
			item: 'Despatch',
			...pnl.despatch,
		},
		{
			item: 'Address Commission',
			...pnl.addressCommission,
		},
		{
			item: 'Broker Commission',
			...pnl.brokerCommission,
		},
		{
			item: 'Total Revenue',
			bold: true,
			...pnl.totalRevenue,
		},
	];

	const expenseItems = hasCargoRelet ? [
		{
			item: 'Other Costs',
			...pnl.otherExpenses,
		},
		{
			item: 'Total Voyage Exp',
			bold: true,
			...pnl.totalExpenses,
		},
	] :  [
		{
			item: 'Port Costs',
			...pnl.expensesInPort,
		},
		{
			item: 'Liner Costs',
			...pnl.linerCosts,
		},
		{
			item: 'Other Expenses',
			...pnl.otherExpenses,
		},
		{
			item: isEuEtsIceClassDiscounted ? 'EU ETS Costs (-5%)' : 'EU ETS',
			...pnl.euEts,
		},
		{
			item: 'UK ETS',
			...pnl.ukEts,
		},
		{
			item: 'Fuel EU Penalty',
			...pnl.fuelEuPenalty,
		},
		{
			item: 'Bunker Expenditure',
			...pnl.bunkerExpenditure,
			...(bunkersGoBelowZero ? {
				icon: faExclamationCircle,
				iconColor: '#ff0000',
				iconTooltip: `
					Forecast bunkers ROB go below zero. When forecast bunkers go below zero, 
					the bunker expenditure calculation stops. You need to add a planned stem 
					to see an accurate bunker expenditure calculation.`,
			} : {})
		},
		...(showTcInDeliveryBunkers ? [
			{
				item: 'TC In Redelivery Bunker Gain / Loss',
				type: 'revenue',
				onClick: handleModalOpen,
				icon: faGasPump,
				...pnl.tcInRedeliveryBunkerGainLoss,
			},
		] : []),
		{
			item: 'Total Expenses',
			bold: true,
			...pnl.totalExpenses,
		},
	];

	const resultsItems = hasCargoRelet ? [
		{
			item: 'Net Result',
			bold: true,
			...pnl.netResult,
		},
	] : [
		{
			item: 'TCE Earnings',
			bold: true,
			...pnl.voyageProfit,
		},
		{
			item: 'Voyage Days',
			number: true,
			bold: true,
			...pnl.voyageDays,
		},
		{
			item: 'TCE / Day',
			highlight: true,
			...pnl.tce,
		},
		...(!isTcInVessel ? [
			{
				item: 'CAPEX',
				...pnl.capex,
			},
			{
				item: 'OPEX',
				...pnl.opex,
			},
		] : []),
		{
			item: 'Net Hire Cost',
			...pnl.tcInCosts,
		},
		{
			item: 'Net Result',
			bold: true,
			...pnl.netResult,
		},
		{
			item: 'Net Result / Day',
			highlight: true,
			...pnl.netResultPerDay,
		},
	];

	return {
		revenueItems,
		expenseItems,
		resultsItems,
	};
};

export default getVcPnlItems;
